import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  Button,
  Input,
  useColorMode,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
} from '@chakra-ui/react'
import { FilePlus, Upload, Link as LinkIcon } from 'lucide-react'
import CursorIcon from '@lobehub/icons/es/Cursor/components/Mono'
import ClaudeColor from '@lobehub/icons/es/Claude/components/Color'
import { toast } from 'sonner'
import { AgentConnectionRow, CursorConnectButton, ClaudeConnectButton } from '@/components/agents'
import type { ShareFormat } from '@core/types/share'
import type { AgentConnection } from '@/lib/api/keys'
import { listApiKeyAgents } from '@/lib/api/keys'
import { getStoredApiKey } from '@/lib/storage/local-storage'
import { isValidTextFile, detectFormatFromFilename } from '@/lib/utils/filename'

interface EditorEmptyStateProps {
  onNewFile: () => void
  onContentLoaded: (content: string, filename?: string, format?: ShareFormat) => void
}

export function EditorEmptyState({ onNewFile, onContentLoaded }: EditorEmptyStateProps) {
  const { colorMode } = useColorMode()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const { isOpen: isUrlOpen, onOpen: onUrlOpen, onClose: onUrlClose } = useDisclosure()
  const [url, setUrl] = useState('')
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [agents, setAgents] = useState<AgentConnection[]>([])

  const isDark = colorMode === 'dark'

  // Load connected agents on mount
  useEffect(() => {
    const apiKey = getStoredApiKey()
    if (apiKey) {
      listApiKeyAgents(apiKey)
        .then((response) => setAgents(response.agents))
        .catch(() => {/* silently fail */})
    }
  }, [])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!isValidTextFile(file.name)) {
      toast.error('Please upload a .md or .mdc file')
      return
    }

    if (file.size > 1024 * 1024) {
      toast.error('File size must be less than 1MB')
      return
    }

    const text = await file.text()
    const format = detectFormatFromFilename(file.name)
    // Keep the full filename including extension
    onContentLoaded(text, file.name, format)
    e.target.value = ''
  }

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const file = e.dataTransfer.files[0]
    if (!file) return

    if (!isValidTextFile(file.name)) {
      toast.error('Please drop a .md or .mdc file')
      return
    }

    if (file.size > 1024 * 1024) {
      toast.error('File size must be less than 1MB')
      return
    }

    const text = await file.text()
    const format = detectFormatFromFilename(file.name)
    // Keep the full filename including extension
    onContentLoaded(text, file.name, format)
  }, [onContentLoaded])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const extractFilenameFromUrl = (urlString: string): string | undefined => {
    try {
      const urlObj = new URL(urlString)
      const lastSegment = urlObj.pathname.split('/').pop()
      if (lastSegment && lastSegment.length > 0 && !/^\d+$/.test(lastSegment)) {
        // Keep the extension in the filename
        return lastSegment
      }
    } catch {
      // Invalid URL
    }
    return undefined
  }

  const handleUrlSubmit = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL')
      return
    }

    setIsLoadingUrl(true)
    try {
      let fetchUrl = url.trim()

      if (fetchUrl.includes('github.com') && !fetchUrl.includes('raw.githubusercontent.com')) {
        fetchUrl = fetchUrl
          .replace('github.com', 'raw.githubusercontent.com')
          .replace('/blob/', '/')
      }

      const response = await fetch(fetchUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`)
      }

      const text = await response.text()
      const filename = extractFilenameFromUrl(fetchUrl)
      const format = filename ? detectFormatFromFilename(filename) : 'md'
      onContentLoaded(text, filename, format)
      toast.success('Content loaded from URL')
      onUrlClose()
      setUrl('')
    } catch {
      toast.error('Failed to fetch content from URL')
    } finally {
      setIsLoadingUrl(false)
    }
  }

  // Listen for paste events to detect share links
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text/plain')?.trim()
      if (!text) return

      // Check if it's a myrkup share link
      try {
        const url = new URL(text)
        if ((url.hostname === 'myrkup.com' || url.hostname === 'localhost') && url.pathname.startsWith('/s/')) {
          e.preventDefault()
          navigate(url.pathname + url.hash)
        }
      } catch {
        // Not a URL, ignore
      }
    }

    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [navigate])

  return (
    <>
      <Flex
        flex={1}
        direction="column"
        align="center"
        justify="center"
        px={8}
        py={12}
      >
        <VStack spacing={8} maxW="480px" w="100%">
          {/* Brand */}
          <Text
            fontSize="2xl"
            fontWeight="bold"
            fontFamily="'Inter', sans-serif"
            color={isDark ? 'stone.100' : 'stone.800'}
            letterSpacing="-0.02em"
          >
            myrkup
          </Text>

          {/* Drop zone */}
          <Box
            w="100%"
            p={10}
            border="2px dashed"
            borderColor={isDragOver
              ? 'brand.500'
              : isDark ? 'stone.600' : 'stone.300'
            }
            borderRadius="xl"
            bg={isDragOver
              ? isDark ? 'brand.900' : 'brand.50'
              : isDark ? 'stone.800' : 'stone.50'
            }
            cursor="pointer"
            transition="all 0.15s ease"
            _hover={{
              borderColor: isDark ? 'stone.500' : 'stone.400',
              bg: isDark ? 'stone.750' : 'stone.100',
            }}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <VStack spacing={3}>
              <Icon
                as={Upload}
                boxSize={8}
                color={isDragOver ? 'brand.500' : isDark ? 'stone.400' : 'stone.500'}
              />
              <VStack spacing={1}>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={isDark ? 'stone.200' : 'stone.700'}
                >
                  Drop a .md or .mdc file here
                </Text>
                <Text
                  fontSize="xs"
                  color={isDark ? 'stone.400' : 'stone.500'}
                >
                  or click to browse
                </Text>
              </VStack>
            </VStack>
          </Box>

          {/* Action buttons */}
          <HStack spacing={3} w="100%">
            <Button
              flex={1}
              leftIcon={<Icon as={FilePlus} boxSize={4} />}
              onClick={onNewFile}
              size="md"
              variant="outline"
              borderColor={isDark ? 'stone.600' : 'stone.300'}
              color={isDark ? 'stone.200' : 'stone.700'}
              _hover={{
                bg: isDark ? 'stone.700' : 'stone.100',
                borderColor: isDark ? 'stone.500' : 'stone.400',
              }}
            >
              New file
            </Button>
            <Button
              flex={1}
              leftIcon={<Icon as={LinkIcon} boxSize={4} />}
              onClick={onUrlOpen}
              size="md"
              variant="outline"
              borderColor={isDark ? 'stone.600' : 'stone.300'}
              color={isDark ? 'stone.200' : 'stone.700'}
              _hover={{
                bg: isDark ? 'stone.700' : 'stone.100',
                borderColor: isDark ? 'stone.500' : 'stone.400',
              }}
            >
              Import from URL
            </Button>
          </HStack>

          {/* AI Integration */}
          <VStack spacing={3} w="100%">
            {/* Show connected agents or connect banners */}
            {agents.some((a) => a.name.toLowerCase().includes('cursor')) ? (
              <AgentConnectionRow
                type="cursor"
                agent={agents.find((a) => a.name.toLowerCase().includes('cursor'))!}
              />
            ) : (
              <HStack
                w="100%"
                p={4}
                bg={isDark ? 'stone.800' : 'stone.50'}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={isDark ? 'stone.700' : 'stone.200'}
                justify="space-between"
              >
                <HStack spacing={3}>
                  <CursorIcon size={24} />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color={isDark ? 'stone.200' : 'stone.700'}>
                      Connect to Cursor
                    </Text>
                    <Text fontSize="sm" color={isDark ? 'stone.400' : 'stone.500'}>
                      Let your AI assistant create and share markdown
                    </Text>
                  </VStack>
                </HStack>
                <CursorConnectButton variant="compact" label="Connect" noIcon />
              </HStack>
            )}

            {agents.some((a) => a.name.toLowerCase().includes('claude')) ? (
              <AgentConnectionRow
                type="claude"
                agent={agents.find((a) => a.name.toLowerCase().includes('claude'))!}
              />
            ) : (
              <HStack
                w="100%"
                p={4}
                bg={isDark ? 'stone.800' : 'stone.50'}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={isDark ? 'stone.700' : 'stone.200'}
                justify="space-between"
              >
                <HStack spacing={3}>
                  <ClaudeColor size={24} />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color={isDark ? 'stone.200' : 'stone.700'}>
                      Connect to Claude
                    </Text>
                    <Text fontSize="sm" color={isDark ? 'stone.400' : 'stone.500'}>
                      Use Claude Code CLI to manage your shares
                    </Text>
                  </VStack>
                </HStack>
                <ClaudeConnectButton variant="compact" label="Connect" noIcon />
              </HStack>
            )}

            {/* Show any other connected agents */}
            {agents
              .filter((a) => !a.name.toLowerCase().includes('cursor') && !a.name.toLowerCase().includes('claude'))
              .map((agent) => (
                <AgentConnectionRow key={agent.name} type="other" agent={agent} />
              ))}
          </VStack>

        </VStack>
      </Flex>

      {/* Hidden file input */}
      <Input
        ref={fileInputRef}
        type="file"
        accept=".md,.mdc,.markdown"
        display="none"
        onChange={handleFileChange}
      />

      {/* URL Import Modal */}
      <Modal isOpen={isUrlOpen} onClose={onUrlClose} size="md">
        <ModalOverlay />
        <ModalContent bg={isDark ? 'stone.800' : 'white'}>
          <ModalHeader>Load from URL</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">URL</FormLabel>
                <Input
                  placeholder="https://example.com/readme.md"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUrlSubmit()
                  }}
                  size="sm"
                />
              </FormControl>
              <Text fontSize="xs" color="stone.500">
                GitHub links are automatically converted to raw content URLs.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUrlClose} size="sm">
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleUrlSubmit}
              isLoading={isLoadingUrl}
              size="sm"
            >
              Load
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
